import { AsyncPaginate } from 'react-select-async-paginate'
import { SelectWrapper } from '../wrapper'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { createSearchParams } from 'react-router-dom'
import { store } from '@/store'
import { contractApi } from '@/store/services'
import PropTypes from 'prop-types'

export const ContractSelector = ({
  label = '',
  onChange,
  value,
  query = {},
  ...props
}) => {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState({})
  const [inputValue, setInputValue] = useState('') // 👈 necesario para paginación infinita

  const getCurrentContract = useCallback(async (contractId) => {
    try {
      const contract = await store
        .dispatch(contractApi.endpoints.getParentsSelectorContract.initiate(contractId))
        .unwrap()

      return contract
    } catch (_) {
      return null
    }
  }, [])

  const formatContractLabel = (contract) => (
    `${contract.id} - ${contract.name}${contract.companyName ? ' - ' + contract.companyName : ''}`
  )

  const loadOptions = async (search, _loadedOptions, additional) => {
    const page = additional?.page || 1

    const normalizedSearch = (search ?? '').trim()
    const isNumericSearch = normalizedSearch !== '' && /^\d+$/.test(normalizedSearch)

    const searchParams = createSearchParams({
      ...query,
      page,
      per_page: 10,
      q: search,
      ...(isNumericSearch ? { id: normalizedSearch } : {}),
      'order[field]': 'id',
      'order[sort]': 'asc'
    }).toString()

    const response = await store
      .dispatch(contractApi.endpoints.getParentsSelectorContracts.initiate(`?${searchParams}`))
      .unwrap()

    const { contracts, meta } = response

    const options = contracts.map((contract) => ({
      value: contract.id,
      label: formatContractLabel(contract)
    }))

    const totalPages = meta?.pages || meta?.last || 1
    const hasMore = page < totalPages

    return {
      options,
      hasMore,
      additional: {
        page: page + 1
      }
    }
  }

  const handleChange = (option) => {
    setSelectedOption(option)
    onChange(option)
  }

  useEffect(() => {
    if (!value) {
      setSelectedOption({})
      return
    }

    if (String(value) === String(selectedOption?.value)) return

    getCurrentContract(value).then((contract) => {
      if (contract && contract.id && contract.name) {
        setSelectedOption({
          value: contract.id,
          label: formatContractLabel(contract),
          resource: contract
        })
      } else {
        setSelectedOption(null)
      }
    })
  }, [value])

  return (
    <SelectWrapper label={label || t('contract.one')} styles={props.styles}>
      {(styles) => (
        <AsyncPaginate
          {...props}
          isClearable
          placeholder={t('select.one')}
          cacheUniqs={[query]}
          styles={styles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuPlacement="auto"
          maxMenuHeight={300}
          loadingMessage={() => t('select.loading')}
          value={isEmpty(selectedOption) ? null : selectedOption}
          onChange={handleChange}
          loadOptions={loadOptions}
          debounceTimeout={400}
          inputValue={inputValue}
          onInputChange={(value) => setInputValue(value)}
          defaultAdditional={{ page: 1 }}
        />
      )}
    </SelectWrapper>
  )
}

ContractSelector.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.number,
  label: PropTypes.string,
  query: PropTypes.object,
  styles: PropTypes.object
}
